import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@trillion/ui/components/ui/button";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { ProductForm } from "@/components/products/product-form";
import { adminApi } from "@/lib/admin-api";
import { describeApiError } from "@/lib/api-error";
import type { Product } from "@/types";

export function ProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const productId = Number(id);

  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchProduct() {
      try {
        const data = await adminApi.products.get(productId);
        setProduct(data);
      } catch (err) {
        toast.error("Failed to load product", {
          description: describeApiError(err),
        });
        navigate("/products");
      } finally {
        setIsLoading(false);
      }
    }
    fetchProduct();
  }, [productId, navigate]);

  async function handleSubmit(data: Partial<Product>) {
    setIsSaving(true);
    try {
      await adminApi.products.update(productId, data);
      toast.success("Product updated successfully");
      navigate("/products");
    } catch (err) {
      toast.error("Failed to update product", {
        description: describeApiError(err),
      });
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-6 py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={`Edit: ${product.name}`}
        description="Update product details"
        actions={
          <Button variant="outline" onClick={() => navigate("/products")}>
            <ArrowLeft className="mr-2 size-4" />
            Back to Products
          </Button>
        }
      />
      <ProductForm
        initialData={product}
        onSubmit={handleSubmit}
        isLoading={isSaving}
      />
    </div>
  );
}
